/**
 * Module Settings Page Wrapper
 *
 * Dynamically loads the settings page for a module based on URL params.
 * Channel-scoped: reads :channelId from the route, with a channel selector
 * fallback when the user has more than one channel.
 *
 * R20 chat 4: a module that declares agent properties (the backend's
 * `agent_properties`) also gets the core-owned AgentPushKeyCard here — even a
 * backend-only module with no frontend entry and no settings page of its own.
 */

import { useParams, Navigate, Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Loader2, AlertCircle, ArrowLeft } from 'lucide-react'
import { getModule } from '@/modules'
import api from '@/api/client'
import AgentPushKeyCard from '@/components/AgentPushKeyCard'

/**
 * Channel info for selector.
 */
interface Channel {
  id: number
  twitch_channel_name: string
}

/** The fields this page reads from GET /api/modules/{name}. */
interface ModuleInfo {
  display_name: string
  agent_properties?: string[]
}

/**
 * Module Settings Wrapper
 *
 * Handles loading the correct module's settings component.
 */
export default function ModuleSettingsWrapper() {
  const { moduleName, channelId: channelIdParam } = useParams<{
    moduleName: string
    channelId?: string
  }>()

  const [channels, setChannels] = useState<Channel[]>([])
  const [selectedChannelId, setSelectedChannelId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<ModuleInfo | null>(null)
  const [infoLoaded, setInfoLoaded] = useState(false)

  // The backend's view of the module: whether it accepts agent pushes. A
  // failure here only hides the push-key card; it is not a page error.
  useEffect(() => {
    if (!moduleName) return
    let cancelled = false
    setInfoLoaded(false)
    api
      .get<ModuleInfo>(`/api/modules/${encodeURIComponent(moduleName)}`)
      .then((data) => { if (!cancelled) setInfo(data) })
      .catch(() => { if (!cancelled) setInfo(null) })
      .finally(() => { if (!cancelled) setInfoLoaded(true) })
    return () => { cancelled = true }
  }, [moduleName])

  // Fetch user's channels
  useEffect(() => {
    async function fetchChannels() {
      try {
        const data = await api.get<Channel[]>('/api/channels')
        setChannels(data)

        // Use URL param if provided, otherwise first channel
        if (channelIdParam) {
          setSelectedChannelId(parseInt(channelIdParam))
        } else if (data.length > 0) {
          setSelectedChannelId(data[0].id)
        }

        setError(null)
      } catch (err) {
        console.error('Failed to fetch channels:', err)
        setError('Failed to load channels')
      } finally {
        setIsLoading(false)
      }
    }

    fetchChannels()
  }, [channelIdParam])

  // Get the module definition
  const module = moduleName ? getModule(moduleName) : undefined
  const agentCapable = (info?.agent_properties?.length ?? 0) > 0
  const displayName = module?.displayName ?? info?.display_name ?? moduleName

  // Where "back" goes: the channel's modules list when we know the channel.
  const backTo = channelIdParam
    ? `/channels/${channelIdParam}/modules`
    : '/channels'

  if (!moduleName) {
    return <Navigate to={backTo} replace />
  }

  if (isLoading || !infoLoaded) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-synth-magenta" size={32} />
      </div>
    )
  }

  if (!module && !agentCapable) {
    return <Navigate to={backTo} replace />
  }

  if (!module?.SettingsPage && !agentCapable) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="mx-auto text-state-warning mb-4" size={48} />
        <h3 className="text-lg font-medium text-text-primary mb-2">
          No settings available
        </h3>
        <p className="text-text-secondary">
          The {displayName} module does not have configurable settings.
        </p>
      </div>
    )
  }

  if (error || channels.length === 0) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="mx-auto text-state-danger mb-4" size={48} />
        <h3 className="text-lg font-medium text-text-primary mb-2">
          {error || 'No channels found'}
        </h3>
        <p className="text-text-secondary">
          {error
            ? 'Could not load your channels. Please try again.'
            : 'You need to add a channel before configuring modules.'}
        </p>
      </div>
    )
  }

  if (!selectedChannelId) {
    return (
      <div className="card text-center py-12">
        <AlertCircle className="mx-auto text-state-warning mb-4" size={48} />
        <h3 className="text-lg font-medium text-text-primary mb-2">
          Select a channel
        </h3>
        <p className="text-text-secondary">
          Choose a channel to configure this module for.
        </p>
      </div>
    )
  }

  const SettingsComponent = module?.SettingsPage

  return (
    <div className="space-y-4">
      {/* Back link */}
      <Link
        to={backTo}
        className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft size={16} />
        Modules
      </Link>

      {/* Channel selector (if multiple channels) */}
      {channels.length > 1 && (
        <div className="flex items-center gap-3">
          <label className="text-sm text-text-secondary">
            Configuring for channel:
          </label>
          <select
            value={selectedChannelId}
            onChange={(e) => setSelectedChannelId(parseInt(e.target.value))}
            className="select w-auto"
          >
            {channels.map((ch) => (
              <option key={ch.id} value={ch.id}>
                #{ch.twitch_channel_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Module settings component */}
      {SettingsComponent ? (
        <SettingsComponent
          channelId={selectedChannelId}
          moduleName={moduleName}
        />
      ) : (
        <h2 className="text-xl font-semibold text-text-primary">{displayName}</h2>
      )}

      {/* Agent push key (R20 chat 4) — core-owned, for agent-capable modules */}
      {agentCapable && (
        <AgentPushKeyCard moduleName={moduleName} channelId={selectedChannelId} />
      )}
    </div>
  )
}
